const m = require('minthril');
const { component } = require('../../component');
const { RouteSearch, RouteTimeline } = require('../../components/route-timeline');
const { SiteNavigation } = require('../../components/site-navigation');
const { RendererHealth } = require('../../components/renderer-health');

const RouteDetailsPage = component((vnode, emit) => {
  let cacheInfo = null;
  let loadedPath = null;
  let loadingCacheInfo = false;
  let screenshotUrl = null;
  let screenshotPath = null;
  let loadingScreenshot = false;
  let loadedScreenshotPath = null;
  let removed = false;

  function clearScreenshotUrl() {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    screenshotUrl = null;
  }

  async function loadCacheInfo(path, onLoadCacheInfo) {
    if (!onLoadCacheInfo || loadingCacheInfo || loadedPath === path) {
      return;
    }

    loadingCacheInfo = true;

    try {
      cacheInfo = await onLoadCacheInfo(path);
      loadedPath = path;
    } catch (error) {
      cacheInfo = null;
      loadedPath = path;
      console.error(`Unable to load cache info for ${path}.`, error);
    } finally {
      loadingCacheInfo = false;
      emit('redraw');
    }
  }

  async function loadScreenshot(path, onLoadScreenshot) {
    if (!onLoadScreenshot || loadingScreenshot || loadedScreenshotPath === path) return;
    loadingScreenshot = true;

    try {
      const screenshot = await onLoadScreenshot(path);
      if (!removed && screenshotPath === path) {
        if (screenshot) screenshotUrl = URL.createObjectURL(screenshot);
        loadedScreenshotPath = path;
      }
    } catch (error) {
      if (!removed && screenshotPath === path) {
        loadedScreenshotPath = path;
        console.error(`Unable to load screenshot for ${path}.`, error);
      }
    } finally {
      loadingScreenshot = false;
      emit('redraw');
    }
  }

  return {
    view: ({ attrs }) => {
      const {
        site,
        path,
        routes,
        onLoad,
        onLoadMore,
        onLoadCacheInfo,
        onLoadScreenshot,
        onClearCache,
        detailsHref,
        backHref = '/sites',
        backLabel = 'All stats',
        overviewHref,
        trafficHref,
        routesHref,
        costHref,
        settingsHref,
        sitesHref,
        routeSettingsHref,
        rendererMetrics,
        usagePeriodHours
      } = attrs;

      if (path !== loadedPath && !loadingCacheInfo) {
        loadCacheInfo(path, onLoadCacheInfo);
      }
      if (path !== screenshotPath) {
        clearScreenshotUrl();
        screenshotPath = path;
        loadedScreenshotPath = null;
      }
      if (path !== loadedScreenshotPath && !loadingScreenshot) {
        loadScreenshot(path, onLoadScreenshot);
      }

      return m('section.panel.site-detail', [
      m(SiteNavigation, {
        breadcrumbs: [
          { label: sitesHref.startsWith('/admin') ? 'Admin' : 'Sites', href: sitesHref },
          { label: site.name, href: overviewHref },
          { label: 'Routes', href: routesHref },
          { label: path }
        ],
        backHref,
        backLabel,
        overviewHref,
        trafficHref,
        routesHref,
        costHref,
        settingsHref,
        current: 'routes'
      }),
      m('h2', site.name),
      m('div.route-details__settings-link',
        [
          m('a', { href: `${routeSettingsHref}?route=${encodeURIComponent(path)}` }, 'Route settings'),
          m('button.route-details__clear-cache', {
            type: 'button',
            onclick: () => onClearCache?.(path)
          }, 'Clear route cache')
        ]),
      m(RouteSearch, { routes, detailsHref }),
      m('section.route-details__screenshot', [
        m('h3', 'Mobile render preview'),
        loadingScreenshot
          ? m('p.muted', 'Loading screenshot…')
          : screenshotUrl
            ? m('img.route-details__screenshot-image', {
              src: screenshotUrl,
              alt: `Mobile screenshot of ${path}`
            })
            : m('p.muted', cacheInfo?.renderer === 'jsdom'
              ? 'Screenshots are unavailable for jsdom renders.'
              : 'A screenshot will appear here after this route is rendered.')
      ]),
      cacheInfo?.renderer && m('p.muted', `Last render: ${cacheInfo.renderer === 'jsdom' ? 'jsdom' : 'Chromium'}.`),
      cacheInfo?.jsdomFallbackReasons?.length > 0 &&
        m('p.muted', `jsdom fallback: ${cacheInfo.jsdomFallbackReasons.join(', ')}`),
      m(RendererHealth, {
        comparison: cacheInfo?.lastRendererComparison,
        metrics: rendererMetrics,
        enabled: cacheInfo?.effectiveRenderer === 'jsdom-first',
        periodHours: usagePeriodHours
      }),
      cacheInfo?.ignoredQueryParameters?.length ? m('p.route-details__query-info', [
        'Learned ignored query parameters: ',
        cacheInfo.ignoredQueryParameters.join(', ')
      ]) : null,
      cacheInfo?.unignorableQueryParameters?.length ? m('p.route-details__query-info', [
        'Unignorable query parameters: ',
        cacheInfo.unignorableQueryParameters.join(', ')
      ]) : null,
        m(RouteTimeline, { path, onLoad, onLoadMore })
      ]);
    },
    onremove: () => {
      removed = true;
      clearScreenshotUrl();
    }
  };
});

module.exports = { RouteDetailsPage };
